import { useCallback, useMemo } from 'react';

import {
  Button,
  CopyButton,
  Icon,
  Link,
  MenuButton,
  MenuItem,
  RyeconCode,
  RyeconCompass,
  RyeconEllipsisHorizontal,
  RyeconLinkExternal,
  RyeconQuery,
  RyeconShare,
  Sizes,
  toast,
} from '@dbt-labs/sourdough';

import { ActionButtons } from '@/components/ActionButtons';
import {
  RyeconFavoriteFilled,
  RyeconFavoriteOutline,
} from '@/context/AccountNavigation/RyeconFavorite';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { usePanel } from '@/context/PanelProvider';
import {
  useAddFavoriteAsset,
  useDeleteFavoriteAsset,
  useFavoriteAssets,
} from '@/hooks/admin/favoriteAssets';
import { useIdeHandoff } from '@/hooks/ide/handoffs';
import { useInsightsDbtHandoff } from '@/hooks/query/handoffs';
import { useOpenIn } from '@/hooks/useOpenIn';
import { useCanvasDbtHandoff } from '@/hooks/visual-editor/handoffs';
import { NavigationResourceType } from '@/panel/ResourcesFilter/selectedResource';
import { getExploreUrlFromQueryUrl } from '@/util/dbt/navigation';

export type ButtonAction =
  | 'explorer'
  | 'ide'
  | 'query'
  | 'canvas-create'
  | 'canvas-edit'
  | 'exposure-dashboard'
  | 'share'
  | 'favorite';

type HeaderButtonsProps = {
  uniqueId?: string;
  resourceType: NavigationResourceType | 'package';
  name: string | null | undefined;
  packageName: string | null | undefined;
  files: (string | null | undefined)[];
  exposureUrl?: string | null;
};

export const HeaderButtons = ({
  uniqueId,
  resourceType,
  name,
  packageName,
  files,
  exposureUrl,
}: HeaderButtonsProps) => {
  const { isSinglePaneView } = usePanel();
  const { showIdeLink, ideLink } = useIdeHandoff({
    files,
    packageName,
    exposureUrl,
  });
  const {
    showInsightsLink,
    insightsHandoffLink,
    isEnabled: isQueryEnabled,
  } = useInsightsDbtHandoff({
    resourceType,
    uniqueId: uniqueId || '',
  });
  const { isEnabled: isVEEnabled } = useCanvasDbtHandoff({
    resourceType,
    uniqueId: uniqueId || '',
    packageName,
  });

  const contextMenuItems = useMemo(
    () => [
      { value: 'explorer', label: 'Open in Explore', ryecon: RyeconCompass },
      {
        value: 'ide',
        label: 'Develop in IDE',
        ryecon: RyeconCode,
        disabled: showIdeLink,
      },
    ],
    [showIdeLink],
  );

  const contextMenuOnClick = useCallback(
    (value: ButtonAction) => {
      if (value === 'explorer') {
        window.location.href = getExploreUrlFromQueryUrl(window.location.href);
      } else if (value === 'ide') {
        window.location.href = ideLink;
      }
    },
    [ideLink],
  );

  return (
    <span className="flex-0 mx-2 flex items-center gap-3">
      {isSinglePaneView ? (
        <>
          <MenuButton
            testId="context-menu"
            items={contextMenuItems}
            onClick={contextMenuOnClick}
            buttonVariant={{ variant: 'ryecon', ryecon: RyeconEllipsisHorizontal }}
            menuPlacement="bottom"
            menuAlignment="right"
          />
          <CopyButton testId="copy-button" inputLabel="Copy" text={name ?? ''} />
        </>
      ) : (
        <>
          {isVEEnabled || isQueryEnabled ? (
            <HeaderButtonsNew
              uniqueId={uniqueId}
              resourceType={resourceType}
              name={name}
              packageName={packageName}
              files={files}
              exposureUrl={exposureUrl}
            />
          ) : (
            <>
              <Button
                text="Share"
                tooltip="Copy link to page"
                ryecon={RyeconShare}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Copied link to clipboard', { id: 'copy-link' });
                }}
                type="secondary"
                size={Sizes.md}
              />
              {exposureUrl && (
                <Link
                  buttonStyleType="secondary"
                  isInternal={false}
                  target="_blank"
                  to={exposureUrl}
                >
                  <Icon ryecon={RyeconLinkExternal} />
                  Open dashboard
                </Link>
              )}
              {showIdeLink && (
                <Link
                  buttonStyleType="secondary"
                  isInternal={false}
                  buttonIsDisabled={!ideLink}
                  to={ideLink}
                  className="whitespace-nowrap"
                  type="link"
                >
                  <Icon ryecon={RyeconCode} />
                  Open in IDE
                </Link>
              )}
              {showInsightsLink && (
                <Link
                  buttonStyleType="primary"
                  isInternal={false}
                  buttonIsDisabled={!insightsHandoffLink}
                  to={insightsHandoffLink}
                  className="whitespace-nowrap"
                  type="link"
                  testId="open-in-insights-button"
                  target="_blank"
                >
                  <Icon ryecon={RyeconQuery} />
                  Open in Insights
                </Link>
              )}
            </>
          )}
        </>
      )}
    </span>
  );
};

export const HeaderButtonsNew = ({
  uniqueId,
  resourceType,
  packageName,
  files,
  name,
  exposureUrl,
}: HeaderButtonsProps) => {
  const { actionResolvers, openInItems, isFetching } = useOpenIn({
    files,
    packageName,
    exposureUrl,
    resourceType,
    uniqueId,
  });

  const { accountId, projectId, environmentId } = useDbtCloudProject();

  const { data: favoriteAssets } = useFavoriteAssets(accountId);

  const isFavorite = favoriteAssets?.some((asset) => asset.asset_id === uniqueId);

  const { mutate: addFavoriteAsset } = useAddFavoriteAsset();
  const { mutate: removeFavoriteAsset } = useDeleteFavoriteAsset();

  const moreItems = useMemo(() => {
    const actions: MenuItem[] = [];
    actions.push({
      label: isFavorite ? 'Remove from favorites' : 'Add to favorites',
      value: 'favorite',
      ryecon: isFavorite ? RyeconFavoriteFilled : RyeconFavoriteOutline,
    });

    // this a fixed action that is always shown
    actions.push({
      label: 'Share',
      value: 'share',
      ryecon: RyeconShare,
    });

    return actions;
  }, [isFavorite]);

  const contextMenuOnClick = useCallback(
    (value: ButtonAction) => {
      switch (value) {
        case 'explorer':
          actionResolvers.explorer();
          break;
        case 'ide':
          actionResolvers.ide();
          break;
        case 'query':
          actionResolvers.query();
          break;
        case 'canvas-create':
          actionResolvers['canvas-create']();
          break;
        case 'canvas-edit':
          actionResolvers['canvas-edit']();
          break;
        case 'exposure-dashboard':
          actionResolvers['exposure-dashboard']();
          break;
        case 'share':
          actionResolvers.share();
          break;
        case 'favorite':
          if (isFavorite) {
            removeFavoriteAsset({
              accountId,
              projectId,
            });
          } else {
            addFavoriteAsset({
              accountId,
              projectId,
              assetId: uniqueId,
              name: name ?? '',
              platform: 'dbt',
              assetType: resourceType,
              environmentId: environmentId?.toString(),
            });
          }
          break;
        default:
          break;
      }
    },
    [
      actionResolvers,
      isFavorite,
      removeFavoriteAsset,
      accountId,
      projectId,
      addFavoriteAsset,
      uniqueId,
      name,
      resourceType,
      environmentId,
    ],
  );

  return (
    <ActionButtons
      fetchingItems={isFetching}
      openInItems={openInItems}
      onClick={contextMenuOnClick}
      moreItems={moreItems}
    />
  );
};
